import React from 'react';
import closeSvg from '../../../../assets/close.svg';

interface SavePresetModalProps {
  onRequestClose: () => void;
  onSubmit: (name: string) => void;
  onSaveAndSend: (name: string) => void;
  canSend: boolean;
  hasNotes: boolean;
  originalName?: string;
}

const SavePresetModal: React.FC<SavePresetModalProps> = ({
  onRequestClose,
  onSubmit,
  onSaveAndSend,
  canSend,
  hasNotes,
  originalName,
}) => {
  const [name, setName] = React.useState(originalName || '');

  const maxReasonLength = 240;

  const modalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onRequestClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onRequestClose]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(16, 29, 59, 0.4)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
    >
      <div style={styles.modalContent} ref={modalRef} onClick={event => event.stopPropagation()}>
        <button
          onClick={onRequestClose}
          style={{
            position: 'absolute',
            top: '1vh',
            right: '1vh',
            backgroundColor: 'transparent',
            border: 'none',
            fontSize: '2vh',
            cursor: 'pointer',
            color: '#333',
          }}
        >
          &times;
        </button>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div
            style={{
              fontWeight: '700',
              fontSize: '2.4vh',
            }}
          >
            Send Preset for Review
          </div>

          <button
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
            onClick={onRequestClose}
          >
            <img src={closeSvg} alt="logout" style={{ width: '2.4vh', height: '2.4vh' }} />
          </button>
        </div>
        <div style={{ minHeight: '1.6vh' }} />
        <div style={styles.label}>Preset name</div>
        <div style={{ minHeight: '0.8vh' }} />
        <input
          placeholder="Add name..."
          value={name}
          style={{
            padding: '1.6vh',
            fontSize: '1.4vh',
            borderRadius: '1.6vh',
            border: '0.1vh solid rgba(16, 29, 59, 0.2)',
            backgroundColor: '#ffffff',
            color: '#101D3B',
            cursor: 'pointer',
            outline: 'none',
            width: '100%',
            boxSizing: 'border-box' as 'border-box',
          }}
          onChange={e => setName(e.target.value)}
        />
        <div style={{ minHeight: '1.6vh' }} />
        {name && hasNotes ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: '0.8vh',
            }}
          >
            {canSend && (
              <button
                onClick={() => onSaveAndSend(name)} // Replace with your function for saving and sending
                style={{
                  width: '100%',
                  minHeight: '5.6vh',
                  color: '#00AB9B',
                  backgroundColor: 'white',
                  border: '0.2vh solid #00AB9B',
                  fontSize: '1.6vh',
                  fontWeight: '700',
                  borderRadius: '1.6vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Send for Approval
              </button>
            )}
            <button
              onClick={() => onSubmit(name)}
              style={{
                width: '100%',
                minHeight: '5.6vh',
                color: 'white',
                backgroundColor: 'rgba(0, 171, 155, 1)',
                fontSize: '1.6vh',
                fontWeight: '700',
                borderRadius: '1.6vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
              }}
            >
              Save
            </button>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: '0.8vh',
            }}
          >
            {canSend && (
              <button
                style={{
                  width: '100%',
                  minHeight: '5.6vh',
                  color: 'rgba(16, 29, 59, 0.4)',
                  backgroundColor: 'white',
                  border: '0.2vh solid rgba(16, 29, 59, 0.1)',
                  fontSize: '1.6vh',
                  fontWeight: '700',
                  borderRadius: '1.6vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Send for Approval
              </button>
            )}
            <button
              style={{
                width: '100%',
                minHeight: '5.6vh',
                color: 'rgba(16, 29, 59, 0.4)',
                backgroundColor: 'rgba(16, 29, 59, 0.05)',
                fontSize: '1.6vh',
                fontWeight: '700',
                borderRadius: '1.6vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
              }}
            >
              Save
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  modalContent: {
    backgroundColor: 'white',
    padding: '2.4vh',
    borderRadius: '1.6vh',
    width: '52vh',
    boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'start',
    color: 'rgba(16, 29, 59, 1)',
    maxHeight: 'calc(100vh - 6.4vh)',
    overflowY: 'auto' as 'auto',
  },
  label: {
    fontSize: '1.2vh',
    fontWeight: '400',
    color: 'rgba(16, 29, 59, 0.6)',
  },
  container: {
    width: '100%',
    display: 'flex',
  },
  select: {
    padding: '1.2vh',
    borderRadius: '1.6vh',
    border: '0.1vh solid rgba(16, 29, 59, 0.2)',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as 'border-box',
  },
  input: {
    fontSize: '1.4vh',
    fontWeight: '400',
    border: 'none',
    backgroundColor: '#ffffff',
    color: 'rgba(16, 29, 59, 1)',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    outline: 'none',
    width: '100%',
  },
  dropdown: {
    position: 'absolute' as 'absolute',
    padding: '0vh 1.6vh',
    top: 'calc(100% - 0.6vh)', // Position it right below the input
    left: '0',
    width: 'calc(100% - 3.2vh)', //compensate padding
    borderTop: 'none',
    maxHeight: '15vh',
    overflowY: 'auto' as 'auto',
    backgroundColor: '#ffffff',
    zIndex: 1,
    borderRadius: '1.6vh',
    boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
  },
  option: {
    padding: '0.8vh 0vh',
    fontWeight: '400',
    fontSize: '1.4vh',
    cursor: 'pointer',
    borderBottom: '0.1vh solid #eee',
    color: '#2d3436',
    textAlign: 'left' as 'left',
  },
  scrollbar: {
    // Empty for now due to different browser specification complexity
  },
};

export default SavePresetModal;
